import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import PremiumSelect from '../components/PremiumSelect';
import { AlertState, Cobranca, CobrancaListResponse, CobrancaStatus } from '../types';
import './Cobrancas.css';

interface CobrancaFormData {
  descricao: string;
  clienteNome: string;
  clienteEmail: string;
  valor: string; // em reais, ex.: "150,00"
  referencia: string;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => Promise<void>;
}

const PAGE_SIZE = 20;

const createEmptyForm = (): CobrancaFormData => ({
  descricao: '',
  clienteNome: '',
  clienteEmail: '',
  valor: '',
  referencia: '',
});

const statusLabels: Record<CobrancaStatus, string> = {
  pendente: 'Pendente',
  pago: 'Pago',
  cancelado: 'Cancelado',
  expirado: 'Expirado',
};

const parseValorToCents = (valor: string): number | null => {
  const normalized = valor.replace(/\s|R\$/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!normalized || Number.isNaN(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100);
};

const formatCents = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const Cobrancas: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [cobrancas, setCobrancas] = useState<Cobranca[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [formData, setFormData] = useState<CobrancaFormData>(createEmptyForm());
  const copiedTimerRef = useRef<number | null>(null);

  const canManage = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr';

  const loadCobrancas = useCallback(
    async (targetPage: number, targetStatus: string): Promise<void> => {
      try {
        const params: Record<string, string | number> = { page: targetPage, limit: PAGE_SIZE };
        if (targetStatus !== 'all') params.status = targetStatus;
        const response = await api.get<CobrancaListResponse>('/cobrancas', { params });
        setCobrancas(response.data.items);
        setTotal(response.data.total);
      } catch (error) {
        console.error('Erro ao carregar cobranças:', error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadCobrancas(page, statusFilter);
  }, [loadCobrancas, page, statusFilter]);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const valorCents = parseValorToCents(formData.valor);
    if (!valorCents) {
      setAlert({ isOpen: true, message: 'Informe um valor válido em reais (ex.: 150,00).', type: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/cobrancas', {
        descricao: formData.descricao.trim(),
        clienteNome: formData.clienteNome.trim(),
        clienteEmail: formData.clienteEmail.trim(),
        valorCents,
        referencia: formData.referencia.trim() || undefined,
      });
      setShowForm(false);
      setFormData(createEmptyForm());
      setPage(1);
      await loadCobrancas(1, statusFilter);
      setAlert({ isOpen: true, message: 'Cobrança criada e link gerado com sucesso!', type: 'success' });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao criar cobrança',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async (cobranca: Cobranca): Promise<void> => {
    if (!cobranca.linkUrl) return;
    try {
      await navigator.clipboard.writeText(cobranca.linkUrl);
      setCopiedId(cobranca.id);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setAlert({ isOpen: true, message: 'Não foi possível copiar o link.', type: 'error' });
    }
  };

  const handleRefreshStatus = async (cobranca: Cobranca): Promise<void> => {
    setRefreshingId(cobranca.id);
    try {
      const response = await api.get<Cobranca>(`/cobrancas/${cobranca.id}`);
      setCobrancas((prev) => prev.map((c) => (c.id === cobranca.id ? response.data : c)));
      if (response.data.status !== cobranca.status) {
        setAlert({
          isOpen: true,
          message: `Status atualizado: ${statusLabels[response.data.status]}.`,
          type: 'success',
        });
      }
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao atualizar status',
        type: 'error',
      });
    } finally {
      setRefreshingId(null);
    }
  };

  const handleCancel = (cobranca: Cobranca): void => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancelar cobrança',
      message: `Deseja cancelar a cobrança "${cobranca.descricao}" de ${cobranca.clienteNome}? O link deixará de aceitar pagamentos.`,
      onConfirm: async () => {
        try {
          await api.post(`/cobrancas/${cobranca.id}/cancelar`);
          await loadCobrancas(page, statusFilter);
          setAlert({ isOpen: true, message: 'Cobrança cancelada.', type: 'success' });
        } catch (error: any) {
          setAlert({
            isOpen: true,
            message: error.response?.data?.message || 'Erro ao cancelar cobrança',
            type: 'error',
          });
        }
      },
    });
  };

  const handleConfirm = async (): Promise<void> => {
    if (!confirmDialog) return;
    setIsConfirming(true);
    try {
      await confirmDialog.onConfirm();
      setConfirmDialog(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const statusFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos os status' },
      { value: 'pendente', label: 'Pendentes' },
      { value: 'pago', label: 'Pagas' },
      { value: 'cancelado', label: 'Canceladas' },
      { value: 'expirado', label: 'Expiradas' },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const stats = useMemo(() => {
    const pendentes = cobrancas.filter((c) => c.status === 'pendente').length;
    const pagas = cobrancas.filter((c) => c.status === 'pago');
    const totalPagoCents = pagas.reduce((sum, c) => sum + c.valorCents, 0);
    return { pendentes, pagasCount: pagas.length, totalPagoCents };
  }, [cobrancas]);

  if (loading) {
    return (
      <div className="cobrancas-state">
        <div className="cobrancas-state-card">
          <span className="cobrancas-badge">Carregando</span>
          <h1>Preparando cobranças</h1>
          <p>Buscando links de pagamento e status junto ao PagBank.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cobrancas-page">
      <section className="cobrancas-hero">
        <div className="cobrancas-hero-copy">
          <span className="cobrancas-badge">Recebimentos</span>
          <h1>Cobranças com link de pagamento</h1>
          <p>
            Gere links de pagamento PagBank (PIX, cartão e boleto), envie ao cliente e acompanhe o
            status de cada cobrança em um só lugar.
          </p>
        </div>
        <div className="cobrancas-hero-side">
          <div className="cobrancas-hero-card">
            <span className="cobrancas-hero-card-label">Nesta página</span>
            <strong>
              {stats.pendentes} pendente(s) · {stats.pagasCount} paga(s)
            </strong>
            <p>
              Recebido (exibido): <span className="cobrancas-hero-emphasis">{formatCents(stats.totalPagoCents)}</span>
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              className={`cobrancas-action-button${showForm ? ' is-secondary' : ''}`}
              onClick={() => setShowForm((open) => !open)}
            >
              {showForm ? 'Fechar formulário' : 'Nova cobrança'}
            </button>
          )}
        </div>
      </section>

      {showForm && canManage && (
        <section className="cobrancas-surface">
          <div className="cobrancas-section-header">
            <div>
              <span className="cobrancas-section-kicker">Cadastro</span>
              <h2>Nova cobrança</h2>
            </div>
            <p>Ao salvar, o link de pagamento é gerado no PagBank e fica pronto para envio.</p>
          </div>

          <form className="cobrancas-form" onSubmit={handleSubmit}>
            <div className="cobrancas-form-grid">
              <div className="form-group cobrancas-span-2">
                <label htmlFor="cobranca-descricao">Descrição *</label>
                <input
                  id="cobranca-descricao"
                  className="premium-text-input"
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex.: Mensalidade de agosto"
                  maxLength={255}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cobranca-nome">Nome do cliente *</label>
                <input
                  id="cobranca-nome"
                  className="premium-text-input"
                  type="text"
                  value={formData.clienteNome}
                  onChange={(e) => setFormData({ ...formData, clienteNome: e.target.value })}
                  placeholder="Ex.: Maria Silva"
                  maxLength={120}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cobranca-email">E-mail do cliente *</label>
                <input
                  id="cobranca-email"
                  className="premium-text-input"
                  type="email"
                  value={formData.clienteEmail}
                  onChange={(e) => setFormData({ ...formData, clienteEmail: e.target.value })}
                  placeholder="cliente@email.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cobranca-valor">Valor (R$) *</label>
                <input
                  id="cobranca-valor"
                  className="premium-text-input"
                  type="text"
                  inputMode="decimal"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  placeholder="Ex.: 150,00"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cobranca-referencia">Referência (opcional)</label>
                <input
                  id="cobranca-referencia"
                  className="premium-text-input"
                  type="text"
                  value={formData.referencia}
                  onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                  placeholder="Ex.: crm:customer:42"
                  maxLength={120}
                />
              </div>
            </div>

            <div className="cobrancas-form-footer">
              <button
                type="button"
                className="cobrancas-form-btn is-ghost"
                onClick={() => {
                  setShowForm(false);
                  setFormData(createEmptyForm());
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="cobrancas-form-btn is-primary" disabled={submitting}>
                {submitting ? 'Gerando link…' : 'Criar e gerar link'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="cobrancas-surface">
        <div className="cobrancas-section-header">
          <div>
            <span className="cobrancas-section-kicker">Histórico</span>
            <h2>Cobranças emitidas</h2>
          </div>
          <p>{total === 0 ? 'Nenhuma cobrança emitida ainda.' : `${total} cobrança(s) no total`}</p>
        </div>

        <div className="cobrancas-toolbar">
          <PremiumSelect
            id="cobrancas-filter-status"
            label="Status"
            wrapperClassName="premium-select-field"
            value={statusFilter}
            options={statusFilterOptions}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            placeholder="Todos os status"
          />
        </div>

        {cobrancas.length === 0 ? (
          <div className="cobrancas-empty-block">
            <strong>Nada por aqui</strong>
            <p>
              {total === 0
                ? canManage
                  ? 'Crie a primeira cobrança para gerar um link de pagamento.'
                  : 'Nenhuma cobrança registrada para a sua organização.'
                : 'Nenhuma cobrança corresponde ao filtro selecionado.'}
            </p>
          </div>
        ) : (
          <div className="cobrancas-grid">
            {cobrancas.map((cobranca) => (
              <article key={cobranca.id} className={`cobranca-card is-${cobranca.status}`}>
                <div className="cobranca-card-header">
                  <div className="cobranca-card-headline">
                    <strong>{cobranca.descricao}</strong>
                    <span>
                      {cobranca.clienteNome} · {cobranca.clienteEmail}
                    </span>
                  </div>
                  <span className={`cobranca-status-badge is-${cobranca.status}`}>
                    {statusLabels[cobranca.status]}
                  </span>
                </div>

                <div className="cobranca-card-body">
                  <div className="cobranca-amount">{formatCents(cobranca.valorCents)}</div>
                  <dl className="cobranca-meta-grid">
                    <div>
                      <dt>Criada em</dt>
                      <dd>{formatDateTime(cobranca.createdAt)}</dd>
                    </div>
                    <div>
                      <dt>{cobranca.status === 'pago' ? 'Paga em' : 'Referência'}</dt>
                      <dd>
                        {cobranca.status === 'pago' && cobranca.paidAt
                          ? formatDateTime(cobranca.paidAt)
                          : cobranca.referencia || '—'}
                      </dd>
                    </div>
                  </dl>

                  <div className="cobranca-card-actions">
                    {cobranca.linkUrl && cobranca.status === 'pendente' && (
                      <button
                        type="button"
                        className="cobranca-card-button is-primary"
                        onClick={() => handleCopyLink(cobranca)}
                      >
                        {copiedId === cobranca.id ? 'Link copiado!' : 'Copiar link'}
                      </button>
                    )}
                    {cobranca.status === 'pendente' && (
                      <button
                        type="button"
                        className="cobranca-card-button"
                        disabled={refreshingId === cobranca.id}
                        onClick={() => handleRefreshStatus(cobranca)}
                      >
                        {refreshingId === cobranca.id ? 'Atualizando…' : 'Atualizar status'}
                      </button>
                    )}
                    {canManage && cobranca.status === 'pendente' && (
                      <button
                        type="button"
                        className="cobranca-card-button is-danger"
                        onClick={() => handleCancel(cobranca)}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="cobrancas-pagination">
            <button
              type="button"
              className="cobranca-card-button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <span>
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              className="cobranca-card-button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Próxima
            </button>
          </div>
        )}
      </section>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
      <ConfirmModal
        isOpen={Boolean(confirmDialog?.isOpen)}
        title={confirmDialog?.title}
        message={confirmDialog?.message || ''}
        confirmLabel="Cancelar cobrança"
        isLoading={isConfirming}
        onClose={() => {
          if (!isConfirming) setConfirmDialog(null);
        }}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default Cobrancas;
